// Knowledge engine — pure functions that link the 47 Gates to ecosystem
// items (music, wisdom, activations, books) via tag overlap. No DB, no
// dependencies. Adding tags or gate synonyms strengthens the graph
// automatically.

import { ECOSYSTEM, type EcosystemItem, type ItemKind } from "./ecosystem";
import { GATES, tagsForGate, type Gate } from "./gates";

function overlap(a: string[], b: Set<string>): number {
  let n = 0;
  for (const t of a) if (b.has(t)) n++;
  return n;
}

/**
 * Ecosystem items whose tags/body resonate with the given Gate.
 * Returns items grouped by kind with a bounded total.
 */
export function itemsForGate(n: number, limit = 12): EcosystemItem[] {
  const gate = GATES.find((g) => g.n === n);
  if (!gate) return [];
  const gateTags = new Set(tagsForGate(gate));
  const titleWord = gate.title.split(" ").pop()?.toLowerCase() ?? "";

  const scored = ECOSYSTEM.map((item) => {
    const tagScore = overlap(item.tags, gateTags);
    // Body match on the titleWord is a lighter signal but recovers items
    // whose tags haven't been authored explicitly.
    const bodyBonus =
      titleWord && item.body && item.body.toLowerCase().includes(titleWord) ? 1 : 0;
    return { item, score: tagScore * 2 + bodyBonus };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  // Preserve variety across kinds.
  const seenKinds = new Set<ItemKind>();
  const out: EcosystemItem[] = [];
  for (const { item } of scored) {
    if (seenKinds.size < 4 && seenKinds.has(item.kind)) continue;
    out.push(item);
    seenKinds.add(item.kind);
    if (out.length >= limit) return out;
  }
  for (const { item } of scored) {
    if (out.includes(item)) continue;
    out.push(item);
    if (out.length >= limit) return out;
  }
  return out;
}

/** Gates that resonate with a given ecosystem item, best-first. */
export function gatesForItem(item: EcosystemItem, limit = 3): Gate[] {
  const itemTags = new Set(item.tags);
  const scored = GATES.map((g) => ({ g, score: overlap(tagsForGate(g), itemTags) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.g);
}
